import React from "react";
import { DEFAULT_DISPLAY } from "./draftModel";
import { includedCards } from "./includedCards";
import { formatValidityMonths, safeHttpUrl } from "../../common/format";

// "**bold**" inside a line becomes <strong>; everything else stays plain text (never HTML).
const inline = (line) =>
  line.split(/\*\*(.+?)\*\*/).map((part, index) => (index % 2 === 1 ? <strong key={index}>{part}</strong> : part));

// The description as the app lays it out: paragraphs split by a blank line, and lines that start
// with "- " as bullet points.
export const DescriptionText = ({ text }) => {
  if (typeof text !== "string" || text.trim() === "") {
    return null;
  }
  const blocks = [];
  text.split(/\n[ \t]*\n/).forEach((paragraph) => {
    let lines = [];
    let bullets = [];
    const flushLines = () => {
      if (lines.length > 0) {
        blocks.push(
          <p key={blocks.length}>
            {lines.map((line, i) => (
              <React.Fragment key={i}>
                {i > 0 && <br />}
                {inline(line)}
              </React.Fragment>
            ))}
          </p>
        );
        lines = [];
      }
    };
    const flushBullets = () => {
      if (bullets.length > 0) {
        blocks.push(
          <ul key={blocks.length}>
            {bullets.map((line, i) => (
              <li key={i}>{inline(line)}</li>
            ))}
          </ul>
        );
        bullets = [];
      }
    };
    paragraph.split("\n").forEach((line) => {
      if (line.startsWith("- ")) {
        flushLines();
        bullets.push(line.slice(2));
      } else if (line.trim() !== "") {
        flushBullets();
        lines.push(line);
      }
    });
    flushLines();
    flushBullets();
  });
  return <div className="warranty-app-description">{blocks}</div>;
};

// Roughly the Amigo Extend warranty screen for this tier: the tier card, then the plan details.
// `content` is what the form sends. `resolved` is a preview sample's draft side, whose `tagline`,
// `hero` and `couponValidityMonths` have the placeholders filled in; null when the preview has none
// of them, and the text then shows as typed. The details, the terms link and the badge are not
// repeated per sample, so they always come from the content.
const AppScreenPreview = ({ content, resolved, sampleLabel }) => {
  const display = content?.display && typeof content.display === "object" ? content.display : DEFAULT_DISPLAY;
  const filled = resolved && typeof resolved === "object" ? resolved : null;
  const tagline = filled ? filled.tagline : display.tagline;
  const heroSource = filled ? filled.hero : display.hero;
  const hero = heroSource && typeof heroSource === "object" ? heroSource : null;
  const details = display.details || DEFAULT_DISPLAY.details;
  const notCovered = Array.isArray(details.notCovered) ? details.notCovered : [];
  const benefits = Array.isArray(content?.benefits) ? content.benefits : [];
  const hasCoupon = benefits.some((benefit) => benefit.kind === "coupon");
  // The "What's included" grid: every free service is one card ("3 Free Services", "Worth ₹2,100").
  const cards = includedCards(
    benefits,
    filled ? { displayName: content?.displayName, couponValueMinor: filled.couponValueMinor } : null
  );
  const validity =
    filled && filled.couponValidityMonths !== undefined
      ? filled.couponValidityMonths
      : hasCoupon
      ? content?.pricingRule?.couponRule?.validityMonths
      : null;
  const termsHref = safeHttpUrl(display.termsUrl);

  return (
    <div className="warranty-app-screen">
      <p className="warranty-help">
        {filled
          ? `Placeholders filled in for ${sampleLabel || "the first sample"}.`
          : "Placeholders such as {tierName} are shown as typed; the app shows them filled in."}
      </p>
      <div className="warranty-app-card">
        <div className="warranty-app-tier">
          <strong>{content?.displayName || "Tier"}</strong>
          {display.bestValue === true && <span className="warranty-badge is-best">Best value</span>}
        </div>
        {tagline && <div className="warranty-app-tagline">{tagline}</div>}
        {hero && (
          <div className="warranty-app-hero">
            {hero.title && <div className="warranty-app-hero-title">{hero.title}</div>}
            {hero.subtitle && <div className="warranty-app-hero-sub">{hero.subtitle}</div>}
          </div>
        )}
        {cards.length > 0 && (
          <ul className="warranty-app-benefits">
            {cards.map((card, index) => (
              <li key={`${card.key}-${index}`}>
                <strong>{card.title}</strong>
                {card.subtitle && <span>{card.subtitle}</span>}
              </li>
            ))}
          </ul>
        )}
        {Number.isInteger(validity) && validity > 0 && (
          <div className="warranty-app-validity">Coupon valid for {formatValidityMonths(validity)}</div>
        )}
      </div>
      {(details.description || notCovered.length > 0 || termsHref) && (
        <div className="warranty-app-details">
          {details.description && (
            <>
              <h6>Plan details</h6>
              <DescriptionText text={details.description} />
            </>
          )}
          {notCovered.length > 0 && (
            <>
              <h6>Not covered</h6>
              <ul>
                {notCovered.map((line, index) => (
                  <li key={index}>{line}</li>
                ))}
              </ul>
            </>
          )}
          {termsHref && (
            <a href={termsHref} target="_blank" rel="noopener noreferrer">
              Terms and conditions
            </a>
          )}
        </div>
      )}
    </div>
  );
};

export default AppScreenPreview;
